import {
    ReactiveDropdownSyncController,
    type ReactiveDropdownSyncFormBuilder,
} from '../dropdown-sync/reactive-dropdown-sync-controller';
import { ReactiveFormHostState } from './reactive-form-host-state';

export interface ReactiveFormHostLifecycleFormBuilder<TModel> extends ReactiveDropdownSyncFormBuilder<TModel> {
    resetExternalDropdowns(): void;
    finalizeExternalDropdowns(): void;
}

export interface ReactiveFormHostLifecycleExternalDropdownOptions<TModel> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly formBuilder: ReactiveFormHostLifecycleFormBuilder<TModel> | null | undefined;
}

export interface ReactiveFormHostLifecycleMinimumReadyOptions<TModel> {
    readonly state: ReactiveFormHostState<TModel>;
    readonly delayMs?: number | undefined;
}

const defaultMinimumReadyDelayMs = 700;

/** Coordinates framework-level host lifecycle events without coupling to application services. */
export class ReactiveFormHostLifecycle {
    public static resetExternalDropdowns<TModel>(options: ReactiveFormHostLifecycleExternalDropdownOptions<TModel>): void {
        options.state.resetExternalDropdowns();
        options.formBuilder?.resetExternalDropdowns();
    }

    public static finalizeExternalDropdowns<TModel>(options: ReactiveFormHostLifecycleExternalDropdownOptions<TModel>): void {
        options.state.finalizeExternalDropdowns();
        this.notifyExternalDropdownsReady(options);
    }

    public static scheduleMinimumReadyTime<TModel>(options: ReactiveFormHostLifecycleMinimumReadyOptions<TModel>): void {
        globalThis.setTimeout(() => {
            options.state.markMinimumReadyTimeElapsed();
        }, options.delayMs ?? defaultMinimumReadyDelayMs);
    }

    private static notifyExternalDropdownsReady<TModel>(options: ReactiveFormHostLifecycleExternalDropdownOptions<TModel>): void {
        globalThis.setTimeout(() => {
            if (!options.state.consumeExternalDropdownsReadyPending()) {
                return;
            }

            options.formBuilder?.finalizeExternalDropdowns();
            ReactiveDropdownSyncController.refreshMappings({ formBuilder: options.formBuilder });
        });
    }
}
